import React, { useState, useEffect, useContext } from "react";
import Modal from "../../components/common/Modal";
import { AuthContext } from "../../context/AuthContext";
import { getSolicitudes, updateEstadoSolicitud } from "../../api/api";

/**
 * Página de gestión de solicitudes para administradores.
 * Permite visualizar, aprobar y rechazar solicitudes de ausencias y vacaciones.
 * Incluye filtros por estado y tipo, y modales de confirmación.
 * 
 * @returns {JSX.Element} Página completa de gestión de solicitudes
 */
export default function SolicitudesPage() {
    const [modalObs, setModalObs] = useState(null);
  const { token } = useContext(AuthContext);
  const [solicitudes, setSolicitudes] = useState([]);
  const [solicitudesFiltradas, setSolicitudesFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todas");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [modalRechazo, setModalRechazo] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [errorRechazo, setErrorRechazo] = useState("");
  const [modalAprobar, setModalAprobar] = useState(null);

  useEffect(() => {
    cargarSolicitudes();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [solicitudes, filtroEstado, filtroTipo]);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const data = await getSolicitudes(token);
      setSolicitudes(data);
    } catch (error) {
      mostrarMensaje("error", "Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let resultado = [...solicitudes];

    if (filtroEstado !== "todas") {
      resultado = resultado.filter((s) => s.estado === filtroEstado);
    }

    if (filtroTipo !== "todos") {
      resultado = resultado.filter((s) => s.tipo === filtroTipo);
    }

    setSolicitudesFiltradas(resultado);
  };

  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: "", texto: "" }), 4000);
  };

  const handleAprobar = (id) => {
    setModalAprobar(id);
  };

  const confirmarAprobar = async () => {
    if (!modalAprobar) return;
    try {
      setLoading(true);
      await updateEstadoSolicitud(token, modalAprobar, "aprobada");
      mostrarMensaje("exito", "Solicitud aprobada correctamente");
      setModalAprobar(null);
      cargarSolicitudes();
    } catch (error) {
      mostrarMensaje("error", error.response?.data?.message || "Error al aprobar solicitud");
    } finally {
      setLoading(false);
    }
  };

  const handleRechazar = async () => {
    if (!motivoRechazo.trim()) {
      setErrorRechazo("Debes indicar un motivo de rechazo");
      return;
    }

    try {
      setLoading(true);
      await updateEstadoSolicitud(token, modalRechazo, "denegada", motivoRechazo);
      mostrarMensaje("exito", "Solicitud rechazada");
      setModalRechazo(null);
      setMotivoRechazo("");
      setErrorRechazo("");
      cargarSolicitudes();
    } catch (error) {
      mostrarMensaje("error", error.response?.data?.message || "Error al rechazar solicitud");
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: "bg-yellow-100 text-yellow-800",
      aprobada: "bg-green-100 text-green-800",
      denegada: "bg-red-100 text-red-800",
    };
    return badges[estado] || "bg-gray-100 text-gray-800";
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const contarPorEstado = (estado) => {
    return solicitudes.filter((s) => s.estado === estado).length;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
          <i className="fas fa-file-alt text-purple-600 text-xl"></i>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestión de Solicitudes</h1>
          <p className="text-sm text-gray-500">Aprueba o deniega solicitudes de empleados</p>
        </div>
      </div>

      {/* Mensajes */}
      {mensaje.texto && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            mensaje.tipo === "exito" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          <i className={`fas fa-${mensaje.tipo === "exito" ? "check-circle" : "exclamation-circle"} mr-2`}></i>
          {mensaje.texto}
        </div>
      )}

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase">Total</p>
              <p className="text-2xl font-bold text-gray-800">{solicitudes.length}</p>
            </div>
            <i className="fas fa-list text-gray-400 text-2xl"></i>
          </div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-yellow-700 uppercase">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-800">{contarPorEstado("pendiente")}</p>
            </div>
            <i className="fas fa-clock text-yellow-400 text-2xl"></i>
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-700 uppercase">Aprobadas</p>
              <p className="text-2xl font-bold text-green-800">{contarPorEstado("aprobada")}</p>
            </div>
            <i className="fas fa-check-circle text-green-400 text-2xl"></i>
          </div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-red-700 uppercase">Denegadas</p>
              <p className="text-2xl font-bold text-red-800">{contarPorEstado("denegada")}</p>
            </div>
            <i className="fas fa-times-circle text-red-400 text-2xl"></i>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
          >
            <option value="todas">Todas</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobada">Aprobadas</option>
            <option value="denegada">Denegadas</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo</label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
          >
            <option value="todos">Todos</option>
            <option value="vacaciones">Vacaciones</option>
            <option value="ausencia">Ausencias</option>
          </select>
        </div>
      </div>

      {/* Lista de Solicitudes */}
      {loading ? (
        <div className="text-center py-8">
          <i className="fas fa-spinner fa-spin text-4xl text-gray-400"></i>
          <p className="text-gray-500 mt-2">Cargando solicitudes...</p>
        </div>
      ) : solicitudesFiltradas.length === 0 ? (
        <div className="text-center py-16">
          <i className="fas fa-inbox text-gray-300 text-6xl mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No hay solicitudes</h3>
          <p className="text-gray-500">No se encontraron solicitudes con los filtros aplicados</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
          <table className="w-full min-w-[1100px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-user mr-2 text-gray-500"></i>Empleado
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-tag mr-2 text-gray-500"></i>Tipo
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-calendar-alt mr-2 text-gray-500"></i>Período
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-info-circle mr-2 text-gray-500"></i>Motivo
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-flag mr-2 text-gray-500"></i>Estado
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-comment mr-2 text-gray-500"></i>Observaciones
                </th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <i className="fas fa-cog mr-2 text-gray-500"></i>Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {solicitudesFiltradas.map((sol) => (
                <tr key={sol._id} className="hover:bg-gray-50 transition">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <i className="fas fa-user text-gray-600 text-xs"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">
                          {sol.usuario?.nombre} {sol.usuario?.primerApellido}
                        </p>
                        <p className="text-xs text-gray-500">{sol.usuario?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${
                      sol.tipo === "vacaciones" 
                        ? "bg-blue-100 text-blue-700" 
                        : "bg-purple-100 text-purple-700"
                    }`}>
                      <i className={`fas ${sol.tipo === "vacaciones" ? "fa-umbrella-beach" : "fa-calendar-times"}`}></i>
                      {sol.tipo === "vacaciones" ? "Vacaciones" : "Ausencia"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-xs text-gray-700">
                      <div className="flex items-center gap-1 mb-1">
                        <i className="fas fa-arrow-right text-green-500 text-[10px]"></i>
                        <span>{formatFecha(sol.fechaInicio)}</span>
                        {sol.tipo === "ausencia" && sol.horaInicio && (
                          <span className="text-gray-500">({sol.horaInicio})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="fas fa-arrow-left text-red-500 text-[10px]"></i>
                        <span>{formatFecha(sol.fechaFin)}</span>
                        {sol.tipo === "ausencia" && sol.horaFin && (
                          <span className="text-gray-500">({sol.horaFin})</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {sol.tipo === "ausencia" ? (
                      <span className="text-xs text-gray-700 font-medium">{sol.motivoAusencia || <span className="text-gray-400 italic">Sin motivo</span>}</span>
                    ) : (
                      <span className="text-gray-400 text-xs italic">N/A</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getEstadoBadge(sol.estado)}`}>
                      {sol.estado.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {/* Observaciones: si es largo, mostrar icono y hacerlo clicable */}
                    {sol.observaciones ? (
                      <>
                        <button
                          type="button"
                          className={`text-xs text-gray-600 max-w-xs truncate text-left w-full ${sol.observaciones.length > 40 ? 'underline decoration-dotted decoration-2 cursor-pointer hover:text-gray-800' : ''}`}
                          title={sol.observaciones}
                          onClick={e => {
                            e.preventDefault();
                            if (sol.observaciones.length > 40) setModalObs(sol.observaciones);
                          }}
                        >
                          {sol.observaciones.length > 40 ? (
                            <span className="inline-flex items-center gap-1">
                              {sol.observaciones.slice(0, 40)}... 
                              <i className="fas fa-eye text-gray-600" title="Ver completo"></i>
                            </span>
                          ) : (
                            sol.observaciones
                          )}
                        </button>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                    {sol.estado === "denegada" && sol.motivoRechazo && (
                      <p className="text-xs text-red-600 mt-1">
                        <strong>Rechazo:</strong> {sol.motivoRechazo}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleAprobar(sol._id)}
                        className={`p-2 bg-green-600 text-white text-xs rounded-lg transition hover:shadow-md ${sol.estado === 'aprobada' ? 'opacity-40 cursor-not-allowed' : 'hover:bg-green-700'}`}
                        title="Aprobar solicitud"
                        disabled={sol.estado === 'aprobada'}
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        onClick={() => setModalRechazo(sol._id)}
                        className={`p-2 bg-red-600 text-white text-xs rounded-lg transition hover:shadow-md ${sol.estado === 'denegada' ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-700'}`}
                        title="Denegar solicitud"
                        disabled={sol.estado === 'denegada'}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal para mostrar observaciones completas */}
      {modalObs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Observaciones completas</h3>
            <div className="bg-gray-100 rounded-lg p-4 text-gray-800 whitespace-pre-line max-h-96 overflow-y-auto">
              {modalObs}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setModalObs(null)} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de aprobación */}
      {modalAprobar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Aprobar Solicitud</h3>
            <p className="mb-6 text-gray-700">¿Estás seguro de que quieres aprobar esta solicitud?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setModalAprobar(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAprobar}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {loading ? "Aprobando..." : "Aprobar Solicitud"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rechazo */}
      {modalRechazo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Motivo de Rechazo</h3>
            <textarea
              value={motivoRechazo}
              onChange={e => {
                setMotivoRechazo(e.target.value);
                if (errorRechazo) setErrorRechazo("");
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-gray-400 focus:border-gray-500"
              rows="4"
              placeholder="Explica por qué se rechaza esta solicitud..."
            ></textarea>
            {errorRechazo && (
              <div className="mt-2 text-red-600 text-sm flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                {errorRechazo}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setModalRechazo(null);
                  setMotivoRechazo("");
                  setErrorRechazo("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleRechazar}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {loading ? "Rechazando..." : "Rechazar Solicitud"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
